import { supabase } from './supabase';
import type { Campaign } from './supabase';
import { sendEmail, emailTemplates } from './email';

export interface Transaction {
  id: string;
  campaignId: string;
  entryId: string;
  amount: number;
  paymentMethod: 'inPerson' | 'online' | 'paypal';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  paypalPaymentId?: string;
  paypalRefundId?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Entry {
  id: string;
  campaign_id: string;
  email: string;
  name: string | null;
  ticket_number: string;
  payment_status: 'pending' | 'paid' | 'failed';
  payment_method: 'online' | 'inPerson';
  total_amount: number;
  quantity: number;
  created_at: string;
}

function generateTicketNumber(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

interface CreateTransactionParams {
  campaignId: string;
  entryId: string;
  amount: number;
  paymentMethod: 'inPerson' | 'online' | 'paypal';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  paypalPaymentId?: string;
  paypalRefundId?: string;
  numberOfTickets: number;
}

export async function createTransaction(transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>): Promise<Transaction> {
  const { data, error } = await supabase
    .from('transactions')
    .insert({
      campaign_id: transaction.campaignId,
      entry_id: transaction.entryId,
      amount: transaction.amount,
      payment_method: transaction.paymentMethod,
      status: transaction.status,
      paypal_payment_id: transaction.paypalPaymentId,
      paypal_refund_id: transaction.paypalRefundId,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateTransactionStatus(
  transactionId: string,
  status: Transaction['status'],
  paypalPaymentId?: string,
  paypalRefundId?: string
): Promise<Transaction> {
  const { data, error } = await supabase
    .from('transactions')
    .update({
      status,
      paypal_payment_id: paypalPaymentId,
      paypal_refund_id: paypalRefundId,
    })
    .eq('id', transactionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getTransaction(transactionId: string): Promise<Transaction> {
  const { data, error } = await supabase
    .from('transactions')
    .select()
    .eq('id', transactionId)
    .single();

  if (error) throw error;
  return data;
}

export async function getTransactionsByCampaign(campaignId: string): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select()
    .eq('campaign_id', campaignId);

  if (error) throw error;
  return data;
}

export async function getTransactionsByEntry(entryId: string): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select()
    .eq('entry_id', entryId);

  if (error) throw error;
  return data;
}

export async function createEntriesForTransaction(
  transaction: Transaction,
  numberOfTickets: number
): Promise<void> {
  const { data: campaign, error: campaignError } = await supabase
    .from('campaigns')
    .select('*')
    .eq('id', transaction.campaignId)
    .single();

  if (campaignError) throw campaignError;

  const entries = Array.from({ length: numberOfTickets }, () => ({
    campaign_id: transaction.campaignId,
    transaction_id: transaction.id,
    status: 'active',
  }));

  const { error: entriesError } = await supabase
    .from('entries')
    .insert(entries);

  if (entriesError) throw entriesError;
}

export async function createTransactionWithEntries(params: CreateTransactionParams): Promise<{ transaction: Transaction }> {
  try {
    // Get entry data first to get email and name
    const { data: entryData, error: entryError } = await supabase
      .from('entries')
      .select('*')
      .eq('id', params.entryId)
      .single();

    if (entryError) throw entryError;

    // Start a transaction block
    const transaction = await createTransaction({
      campaignId: params.campaignId,
      entryId: params.entryId,
      amount: params.amount,
      paymentMethod: params.paymentMethod,
      status: params.status,
      paypalPaymentId: params.paypalPaymentId,
      paypalRefundId: params.paypalRefundId,
    });
    
    // Create entries and get their data
    const { data: entries, error: entriesError } = await supabase
      .from('entries')
      .insert(
        Array.from({ length: params.numberOfTickets }, () => ({
          campaign_id: params.campaignId,
          transaction_id: transaction.id,
          status: 'active',
          ticket_number: generateTicketNumber(),
          email: entryData.email,
          name: entryData.name,
          payment_status: 'paid',
          payment_method: params.paymentMethod,
          total_amount: params.amount,
          quantity: params.numberOfTickets
        }))
      )
      .select();

    if (entriesError) throw entriesError;

    // Get campaign data
    const { data: campaign, error: campaignError } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', params.campaignId)
      .single();

    if (campaignError) throw campaignError;

    // Send a single email with all ticket numbers
    await sendEmail({
      to: entryData.email,
      ...emailTemplates.entryConfirmation(entries, campaign)
    });

    return { transaction };
  } catch (error) {
    console.error('Error creating transaction with entries:', error);
    throw error;
  }
} 